import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import type { EcnRobotDto, EcnSummaryDto, RobotEcnDto } from '@bhops/shared';
import { ecnsApi } from '@/api/ecns';
import { robotEcnsApi } from '@/api/robot-ecns';
import { robotsApi } from '@/api/robots';
import { extractApiError } from '@/api/client';
// 추가수정 2차(26-07-27) 정정 — 표시 날짜·'오늘'은 KST 정본으로.
import { kstTodayYmd, kstYmd } from '@/lib/format-date';
import { Button, FormError, Input } from '@/components/Form';
import { Icon } from '@/components/Icon';
import { Modal } from '@/components/Modal';
import { EcnCreateFields } from '../manufacturing/EcnCatalogPage';
import { useToast } from '@/components/Toast';
import {
  AddButton,
  PickList,
  RobotStage,
  SideColumn,
  boardGridStyle,
  cardStyle,
  emptyStyle,
  panelStyle,
  type PickItem,
  type RobotStageHandle,
} from './robot-board';

// Phase 2.4 — 적용 변경점(ECN) 정방향 태깅 + 역추적. 부품 LOT recall 과는 직교한 별도 보드.
//
// 2026-10-28 오너 "적용 변경점 부분도 동일한 방식으로" — 부품 장착 보드와 같은 모양으로 바꿨다.
//   왼쪽: 적용할 수 있는 변경점. 기본은 이 로봇 모델 + 전 모델 공통(개선계획 §4), [다른 모델까지]로 넓힌다.
//         이미 적용된 것은 '적용됨 · 날짜'.
//   가운데: 로봇 그림 — 끌어다 놓으면 적용일을 고르는 확인 창.
//   오른쪽: 적용된 변경점(역추적 · 해제). 종전 '+ 변경점 추가' 검색 창을 대신한다.

export const ECN_DRAG_TYPE = 'application/x-bhops-ecn-ids';

// ECN 한 줄 표기 — "YYYY-MM-DD · 설명 (코드)". leadingDate 가 없으면 날짜 토큰
// 생략. EcnCatalogPage.formatEcnLabel 과 동일한 규칙(로봇별 적용일 주입 가능).
export function formatEcnLine(
  parts: { code: string; description: string },
  leadingDate: string | null,
): string {
  // 추가수정 2차(26-07-27) 정정 — appliedAt 은 진짜 타임스탬프(서버 now 또는
  // 로컬 자정 ISO)라 UTC 로 자르면 한국 오후/저녁 적용분이 하루 이르게 찍혔다.
  // 구분자는 카탈로그(EcnCatalogPage.formatEcnLabel)와 같은 하이픈 유지 —
  // 한 줄 표기는 여러 화면에서 같은 문자열이어야 한다.
  const date = kstYmd(leadingDate)?.replace(/\./g, '-') ?? null;
  const head = date ? `${date} · ${parts.description}` : parts.description;
  return `${head} (${parts.code})`;
}

export function EcnApplyBoard({ robotId, canEdit }: { robotId: string; canEdit: boolean }) {
  const qc = useQueryClient();
  const [dragging, setDragging] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [applying, setApplying] = useState<EcnSummaryDto[]>([]);
  const [traceEcn, setTraceEcn] = useState<{ ecnId: string; code: string } | null>(null);
  const [fresh, setFresh] = useState<string[]>([]);
  // 새 변경점 추가(같은 날 오너 "여기에 변경점 추가 버튼도 넣어야해") — 변경점 화면의 추가 칸과 같은 것.
  // 이 로봇의 모델을 미리 골라 두고, 추가하면 목록이 새로 와 바로 끌어다 적용할 수 있다.
  const [createOpen, setCreateOpen] = useState(false);
  const stageRef = useRef<RobotStageHandle>(null);

  const appliedQ = useQuery({
    queryKey: ['robot-ecns', robotId],
    queryFn: () => robotEcnsApi.list(robotId),
    staleTime: 30_000,
  });
  const applied = useMemo(() => appliedQ.data?.items ?? [], [appliedQ.data]);
  const appliedByEcn = useMemo(() => new Map(applied.map((a) => [a.ecnId, a])), [applied]);

  // 로봇 상세(RobotDetailPage)와 같은 queryKey — react-query 가 디듀프하므로
  // 탭을 열어둔 상태에선 추가 네트워크가 없다.
  const robotQ = useQuery({
    queryKey: ['robot', robotId],
    queryFn: () => robotsApi.get(robotId),
    staleTime: 60_000,
  });
  const modelId = showAll ? undefined : robotQ.data?.model?.id;
  // 모델을 알기 전에 조회하면 넓은 결과가 한 번 깜빡였다가 좁혀진다 — 대기.
  const scopeReady = showAll || !robotQ.isPending;

  const ecnsQ = useQuery({
    queryKey: ['ecns', 'picker', modelId ?? '', ''],
    queryFn: () => ecnsApi.list({ modelId }),
    enabled: canEdit && scopeReady,
    staleTime: 30_000,
  });
  // ECN 카탈로그를 로봇/로더가 공유하므로, 로더 개조에만 걸린 변경점은 모델
  // 스코프에서 걷어낸다. [다른 모델까지]를 켜면 그대로 나온다(하드 가드 아님).
  const ecns = useMemo(
    () => (ecnsQ.data?.items ?? []).filter((e) => showAll || !e.loaderRevLinkCount),
    [ecnsQ.data, showAll],
  );

  const items = useMemo<PickItem[]>(
    () =>
      ecns.map((e) => {
        const done = appliedByEcn.get(e.id);
        const first = kstYmd(e.firstAppliedAt);
        return {
          id: e.id,
          title: e.description,
          meta: `${e.code}${first ? ` · ${first}` : ''} · 적용 ${e.appliedActiveRobotCount}대`,
          searchText: e.code,
          badge: done ? `적용됨 · ${kstYmd(done.appliedAt) ?? '-'}` : null,
          hint: done ? '이 로봇에 이미 적용된 변경점입니다.' : formatEcnLine(e, e.firstAppliedAt),
        };
      }),
    [ecns, appliedByEcn],
  );

  useEffect(() => {
    if (fresh.length === 0) return;
    const t = window.setTimeout(() => setFresh([]), 2400);
    return () => window.clearTimeout(t);
  }, [fresh]);

  const toast = useToast();
  function openApply(ids: string[]): void {
    // 이미 적용된 것은 서버가 409 로 막는다 — 미리 걸러 확인 창에 올리지 않는다.
    const picked = ecns.filter((e) => ids.includes(e.id) && !appliedByEcn.has(e.id));
    if (picked.length > 0) setApplying(picked);
    else if (ids.length > 0) toast.info('이미 이 로봇에 적용된 변경점입니다.');
  }

  const removeM = useMutation({
    mutationFn: (robotEcnId: string) => robotEcnsApi.remove(robotId, robotEcnId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['robot-ecns', robotId] }),
  });

  function onRemove(item: RobotEcnDto): void {
    if (!window.confirm(`적용된 변경점을 해제할까요?\n${formatEcnLine(item, item.appliedAt)}`)) {
      return;
    }
    removeM.mutate(item.robotEcnId);
  }

  return (
    <div>
      <div style={boardGridStyle(canEdit)}>
        {canEdit && (
          <PickList
            title="적용할 수 있는 변경점"
            items={items}
            loading={ecnsQ.isLoading}
            error={ecnsQ.error ? extractApiError(ecnsQ.error) : null}
            emptyText="이 모델에 맞는 변경점이 없습니다."
            searchPlaceholder="설명이나 코드로 찾기"
            dragType={ECN_DRAG_TYPE}
            icon="scroll"
            actionVerb="적용"
            badgeLabel="적용됨"
            testIdPrefix="ecn"
            toolbar={
              <AddButton
                label="변경점 추가"
                onClick={() => setCreateOpen(true)}
                testId="ecn-create"
              />
            }
            footer={
              // 기본은 이 로봇 모델 + 전 모델 공통. 켜면 모든 변경점이 나온다.
              <label style={showAllStyle}>
                <input
                  type="checkbox"
                  checked={showAll}
                  onChange={(e) => setShowAll(e.target.checked)}
                  aria-label="다른 모델의 변경점까지 보기"
                  data-testid="ecn-show-all"
                />
                다른 모델의 변경점까지 보기
              </label>
            }
            onDragState={setDragging}
            onAction={openApply}
          />
        )}

        <div style={{ ...panelStyle, display: 'flex', gap: 12, padding: 12 }}>
          <RobotStage
            ref={stageRef}
            robotId={robotId}
            dragType={ECN_DRAG_TYPE}
            canDrop={canEdit}
            dragging={dragging}
            hints={{
              idle: '왼쪽 변경점을 로봇에 끌어다 놓으면 적용합니다',
              ready: '여기로 가져오세요',
              over: '놓으면 적용합니다',
              denied: '변경점 적용 권한이 없습니다.',
            }}
            nameOf={(id) => ecns.find((e) => e.id === id)?.code}
            onDropIds={(ids) => {
              setDragging(false);
              openApply(ids);
            }}
            testId="ecn-drop-zone"
          />
          <SideColumn title="적용된 변경점" count={applied.length}>
            {appliedQ.isLoading ? (
              <div style={emptyStyle}>불러오는 중…</div>
            ) : appliedQ.error ? (
              <div style={{ ...emptyStyle, color: 'var(--fg-danger)' }}>
                {extractApiError(appliedQ.error)}
              </div>
            ) : applied.length === 0 ? (
              <div style={emptyStyle}>적용된 변경점이 없습니다.</div>
            ) : (
              applied.map((item) => {
                const freshIndex = fresh.indexOf(item.robotEcnId);
                return (
                  <div
                    key={item.robotEcnId}
                    data-testid={`ecn-row-${item.ecnId}`}
                    className={freshIndex >= 0 ? 'pib-card-new' : undefined}
                    style={{
                      ...cardStyle,
                      animationDelay: freshIndex >= 0 ? `${freshIndex * 60}ms` : undefined,
                    }}
                  >
                    <div style={{ fontSize: 13, lineHeight: 1.4 }}>
                      {formatEcnLine(item, item.appliedAt)}
                      {item.note && (
                        <span style={{ color: 'var(--fg-muted)', marginLeft: 6 }}>
                          · {item.note}
                        </span>
                      )}
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
                      <button
                        type="button"
                        onClick={() => setTraceEcn({ ecnId: item.ecnId, code: item.code })}
                        style={traceBtnStyle}
                        data-testid={`ecn-trace-${item.ecnId}`}
                      >
                        역추적
                      </button>
                      {canEdit && (
                        <button
                          type="button"
                          onClick={() => onRemove(item)}
                          disabled={removeM.isPending}
                          aria-label="변경점 해제"
                          title="변경점 해제"
                          style={ecnRemoveBtnStyle}
                          data-testid={`ecn-remove-${item.ecnId}`}
                        >
                          <Icon name="x" size={13} aria-hidden />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })
            )}
            {removeM.isError && (
              <div style={{ ...emptyStyle, color: 'var(--fg-danger)' }}>
                {extractApiError(removeM.error)}
              </div>
            )}
          </SideColumn>
        </div>
      </div>

      <EcnApplyModal
        ecns={applying}
        robotId={robotId}
        onClose={() => setApplying([])}
        onApplied={(ids) => {
          setFresh(ids);
          stageRef.current?.burst();
        }}
      />
      <Modal open={createOpen} onClose={() => setCreateOpen(false)} title="변경점 추가" width={680}>
        {createOpen && (
          <EcnCreateFields
            initialModelIds={robotQ.data?.model?.id ? [robotQ.data.model.id] : []}
            onCreated={(ecn) => {
              qc.invalidateQueries({ queryKey: ['ecns'] });
              setCreateOpen(false);
              toast.success(`${ecn.code} 변경점을 추가했습니다. 로봇에 끌어다 놓으면 적용됩니다.`);
            }}
            onCancel={() => setCreateOpen(false)}
          />
        )}
      </Modal>
      <EcnReverseTraceModal
        ecn={traceEcn}
        onClose={() => setTraceEcn(null)}
        currentRobotId={robotId}
      />
    </div>
  );
}

// ── 놓은 뒤 확인 창 — 적용일 하나만 고른다(종전 추가 창과 같은 기본값: 오늘). ──

function EcnApplyModal({
  ecns,
  robotId,
  onClose,
  onApplied,
}: {
  ecns: EcnSummaryDto[];
  robotId: string;
  onClose: () => void;
  onApplied: (robotEcnIds: string[]) => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const open = ecns.length > 0;
  const [rows, setRows] = useState<EcnSummaryDto[]>([]);
  // 추가수정 2차(26-07-27) 정정 — date input 기본값의 '오늘'은 공용 kstTodayYmd 하나만 쓴다.
  const [appliedAt, setAppliedAt] = useState(kstTodayYmd());
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setRows(ecns);
    setAppliedAt(kstTodayYmd());
    setError(null);
  }, [open, ecns]);

  function handleClose(): void {
    if (!pending) onClose();
  }

  async function handleSubmit(): Promise<void> {
    if (pending || rows.length === 0) return;
    setPending(true);
    setError(null);
    let done = 0;
    const created: string[] = [];
    try {
      for (const e of rows) {
        const res = await robotEcnsApi.apply(robotId, {
          ecnId: e.id,
          // date input(YYYY-MM-DD) → 로컬 자정 ISO. 미지정이면 서버 now 지만 항상 보냄.
          appliedAt: appliedAt ? new Date(`${appliedAt}T00:00:00`).toISOString() : undefined,
        });
        if (res?.robotEcnId) created.push(res.robotEcnId);
        done += 1;
      }
    } catch (err) {
      // 이미 적용된 줄은 빼서, 다시 누르면 남은 것만 적용되게 한다.
      setRows((rs) => rs.slice(done));
      const reason = extractApiError(err);
      setError(done > 0 ? `${done}개를 적용한 뒤 멈췄습니다. ${reason}` : reason);
    } finally {
      setPending(false);
      if (done > 0) {
        qc.invalidateQueries({ queryKey: ['robot-ecns', robotId] });
        qc.invalidateQueries({ queryKey: ['ecns'] });
        onApplied(created);
      }
    }
    if (done === rows.length) {
      toast.success(done > 1 ? `변경점 ${done}개를 적용했습니다.` : '변경점을 적용했습니다.');
      onClose();
    }
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={rows.length > 1 ? `변경점 ${rows.length}개 적용` : '변경점 적용'}
      width={560}
      footer={
        <>
          <Button type="button" variant="secondary" onClick={handleClose} disabled={pending}>
            취소
          </Button>
          <Button
            type="button"
            onClick={() => void handleSubmit()}
            disabled={pending || rows.length === 0 || !appliedAt}
            data-testid="ecn-apply-submit"
          >
            {pending ? '적용 중…' : '적용'}
          </Button>
        </>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void handleSubmit();
        }}
        style={{ display: 'flex', flexDirection: 'column', gap: 10 }}
      >
        {error && <FormError>{error}</FormError>}
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
          적용일
          <Input
            type="date"
            value={appliedAt}
            onChange={(e) => setAppliedAt(e.target.value)}
            aria-label="적용일"
            data-testid="ecn-apply-date"
            style={{ width: 170 }}
          />
        </label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {rows.map((e) => (
            <div key={e.id} style={ecnApplyRowStyle} data-testid={`ecn-apply-row-${e.id}`}>
              {formatEcnLine(e, e.firstAppliedAt)}
            </div>
          ))}
        </div>
      </form>
    </Modal>
  );
}

// 역추적 모달 — ecnsApi.listRobots(ecnId). 같은 변경점이 적용된 다른 로봇을
// "#serial (modelCode) · appliedAt" 로, robotId → /robots/:id 링크. 카탈로그
// recall 을 재구현하지 않고 기존 endpoint 를 그대로 호출한다.
function EcnReverseTraceModal({
  ecn,
  onClose,
  currentRobotId,
}: {
  ecn: { ecnId: string; code: string } | null;
  onClose: () => void;
  currentRobotId: string;
}) {
  const open = ecn !== null;
  const traceQ = useQuery({
    queryKey: ['ecn-robots', ecn?.ecnId],
    queryFn: () => ecnsApi.listRobots(ecn!.ecnId),
    enabled: open,
    staleTime: 30_000,
  });

  const robots = traceQ.data?.items ?? [];

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`${ecn?.code ?? ''} 변경점이 적용된 로봇`}
      width={560}
    >
      {traceQ.isLoading ? (
        <div style={emptyRowSm}>불러오는 중…</div>
      ) : traceQ.error ? (
        <div style={{ ...emptyRowSm, color: 'var(--fg-danger)' }}>
          {extractApiError(traceQ.error)}
        </div>
      ) : robots.length === 0 ? (
        <div style={emptyRowSm}>이 변경점이 적용된 로봇이 없습니다.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {robots.map((r: EcnRobotDto) => {
            const serial = r.serialShort ?? r.serialLong ?? r.robotId.slice(0, 8);
            const isCurrent = r.robotId === currentRobotId;
            return (
              <Link
                key={r.robotId}
                to={`/robots/${r.robotId}`}
                onClick={onClose}
                style={{
                  ...traceRowStyle,
                  fontWeight: isCurrent ? 700 : 400,
                }}
                data-testid={`ecn-trace-robot-${r.robotId}`}
              >
                <span style={{ fontFamily: 'var(--font-mono)' }}>#{serial}</span>
                <span style={{ color: 'var(--fg-muted)' }}>({r.modelCode || '—'})</span>
                <span style={{ color: 'var(--fg-muted)', marginLeft: 'auto' }}>
                  {/* 추가수정 2차(26-07-27) 정정 — 변경점 카탈로그의 역추적 표
                      (EcnCatalogPage)와 같은 데이터·같은 표기(KST 점 구분)로. */}
                  {kstYmd(r.appliedAt) ?? '—'}
                </span>
                {isCurrent && <span style={{ fontSize: 11, color: 'var(--blue-500)' }}>현재</span>}
              </Link>
            );
          })}
        </div>
      )}
    </Modal>
  );
}

const showAllStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  fontSize: 12,
  color: 'var(--fg-muted)',
};

const traceBtnStyle: CSSProperties = {
  padding: '2px 8px',
  fontSize: 11,
  border: '1px solid var(--border-default)',
  background: 'transparent',
  color: 'var(--fg-default)',
  borderRadius: 'var(--r-btn)',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  flexShrink: 0,
};

const ecnRemoveBtnStyle: CSSProperties = {
  padding: '2px 8px',
  fontSize: 13,
  lineHeight: 1,
  border: '1px solid var(--fg-danger)',
  background: 'transparent',
  color: 'var(--fg-danger)',
  borderRadius: 'var(--r-btn)',
  cursor: 'pointer',
  flexShrink: 0,
};

const ecnApplyRowStyle: CSSProperties = {
  padding: '8px 12px',
  fontSize: 13,
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--r-card)',
  background: 'var(--bg-surface)',
};

const traceRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '6px 8px',
  fontSize: 13,
  borderRadius: 'var(--r-card)',
  textDecoration: 'none',
  color: 'var(--fg-default)',
  borderBottom: '1px solid var(--border-subtle)',
};

const emptyRowSm: CSSProperties = {
  padding: 16,
  textAlign: 'center',
  fontSize: 12,
  color: 'var(--fg-muted)',
};
